import React, { useState } from 'react';
import { Input, Button, Space, Card, Table, Empty, Switch } from 'antd';
import { SearchOutlined, ClearOutlined, PrinterOutlined } from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import PermissionGuard from '../../components/PermissionGuard';
import { Modules } from '../../constants/permissions';
import { usePW9304Query } from './hooks/usePW9304Query';
import type { NotificationRow } from '../../services/pw9304Api';
import './PW9304Form.css';

const PW9304Form: React.FC = () => {
  const { loading, data, queried, noData, runQuery, reset } = usePW9304Query();
  const [docFrom, setDocFrom] = useState('');
  const [docTo, setDocTo] = useState('');
  const [unprintedOnly, setUnprintedOnly] = useState(true);

  const handleQuery = () => {
    runQuery({
      docFrom: docFrom.trim() || undefined,
      docTo: docTo.trim() || undefined,
      unprintedOnly,
    });
  };
  const handleReset = () => { setDocFrom(''); setDocTo(''); setUnprintedOnly(true); reset(); };

  const handlePrint = () => {
    if (!data || data.length === 0) return;
    const win = window.open('', '_blank');
    if (!win) return;
    win.document.write(`<!DOCTYPE html><html><head><title>人事通知單</title>
<style>body{font-family:'Microsoft JhengHei',sans-serif;margin:20px;font-size:12px}h2{text-align:center}table{width:100%;border-collapse:collapse}th,td{border:1px solid #333;padding:3px 6px}th{background:#f0f0f0;text-align:center}.right{text-align:right}@media print{body{margin:10mm}}</style></head><body>
<h2>人事通知單印表</h2>
<table><thead><tr><th>異動單號</th><th>說明</th><th>生效日</th><th>部門</th><th>職位</th><th>員工數</th><th>列印日期</th></tr></thead><tbody>
${data.map(r=>`<tr><td>${r.doc_no}</td><td>${r.doc_desc}</td><td>${r.effect_date?.substring(0,10)||''}</td><td>${r.dept_name}</td><td>${r.title_name}</td><td class="right">${r.emp_count}</td><td>${r.print_date?.substring(0,10)||''}</td></tr>`).join('')}
</tbody></table></body></html>`);
    win.document.close(); win.focus(); win.print();
  };

  const columns: ColumnsType<NotificationRow> = [
    { title: '異動單號', dataIndex: 'doc_no', key: 'doc_no', width: 120 },
    { title: '說明', dataIndex: 'doc_desc', key: 'doc_desc', ellipsis: true },
    { title: '生效日', dataIndex: 'effect_date', key: 'effect_date', width: 110, render: (v: string) => v?.substring(0,10)||'' },
    { title: '部門', dataIndex: 'dept_name', key: 'dept_name', width: 120 },
    { title: '職位', dataIndex: 'title_name', key: 'title_name', width: 100 },
    { title: '員工數', dataIndex: 'emp_count', key: 'emp_count', width: 70, align: 'right' as const },
    { title: '列印日期', dataIndex: 'print_date', key: 'print_date', width: 110, render: (v: string) => v?.substring(0,10)||'' },
  ];

  return (
    <PermissionGuard module={Modules.PW9304} action="query">
      <div className="pw9304-container">
        <div className="pw9304-header">
          <h2>人事通知單印表</h2>
          {queried && !noData && <Button icon={<PrinterOutlined />} onClick={handlePrint}>列印</Button>}
        </div>
        <Card size="small" style={{ marginBottom: 16 }}>
          <Space wrap size="middle">
            <div>
              <label style={{ display: 'block', fontSize: 12, color: '#666', marginBottom: 4 }}>異動單號起</label>
              <Input value={docFrom} onChange={e => setDocFrom(e.target.value)} placeholder="起始單號" style={{ width: 140 }} allowClear />
            </div>
            <div>
              <label style={{ display: 'block', fontSize: 12, color: '#666', marginBottom: 4 }}>異動單號迄</label>
              <Input value={docTo} onChange={e => setDocTo(e.target.value)} placeholder="結束單號" style={{ width: 140 }} allowClear />
            </div>
            <div>
              <label style={{ display: 'block', fontSize: 12, color: '#666', marginBottom: 4 }}>僅未列印</label>
              <Switch checked={unprintedOnly} onChange={setUnprintedOnly} />
            </div>
            <div style={{ display: 'flex', alignItems: 'flex-end', paddingTop: 18 }}>
              <Space>
                <Button type="primary" icon={<SearchOutlined />} onClick={handleQuery} loading={loading}>查詢</Button>
                <Button icon={<ClearOutlined />} onClick={handleReset}>清除</Button>
              </Space>
            </div>
          </Space>
        </Card>
        {queried && noData && <Empty description="查無此資料" style={{ marginTop: 40 }} />}
        {queried && !noData && (
          <Table<NotificationRow> columns={columns} dataSource={data} rowKey="doc_no" loading={loading} size="small"
            pagination={{ pageSize: 50, showSizeChanger: true, showTotal: t => `共 ${t} 筆` }} scroll={{ x: 800 }} />
        )}
      </div>
    </PermissionGuard>
  );
};

export default PW9304Form;
